/**
 * Web Push subscription helper.
 *
 * Asks the user for notification permission, registers the
 * `/sw-push.js` service worker, subscribes against the VAPID public
 * key fetched from the backend, and POSTs the subscription so the
 * server can later push messages to it.
 */
import { axios as http } from "@/services";

const SW_PATH = "/sw-push.js";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
	const raw = window.atob(base64);
	const out = new Uint8Array(raw.length);
	for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
	return out;
}

export function isPushSupported(): boolean {
	return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function getPushPermissionState(): Promise<NotificationPermission> {
	if (!isPushSupported()) return "denied";
	return Notification.permission;
}

async function fetchVapidPublicKey(): Promise<string | null> {
	const res = await http.get("/api/v1/push-subscriptions/public-key");
	return res?.data?.data?.public_key || null;
}

export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
	if (!isPushSupported()) return { ok: false, reason: "unsupported" };

	const permission = await Notification.requestPermission();
	if (permission !== "granted") return { ok: false, reason: "denied" };

	const publicKey = await fetchVapidPublicKey();
	if (!publicKey) return { ok: false, reason: "no-vapid-key" };

	const reg = await navigator.serviceWorker.register(SW_PATH);
	await navigator.serviceWorker.ready;

	let sub = await reg.pushManager.getSubscription();
	if (!sub) {
		sub = await reg.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: urlBase64ToUint8Array(publicKey),
		});
	}

	const json = sub.toJSON();
	await http.post("/api/v1/push-subscriptions", {
		endpoint: json.endpoint,
		keys: json.keys,
		user_agent: navigator.userAgent,
	});

	return { ok: true };
}

export async function disablePush(): Promise<void> {
	if (!isPushSupported()) return;
	const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
	const sub = reg ? await reg.pushManager.getSubscription() : null;
	if (sub) {
		const endpoint = sub.endpoint;
		await sub.unsubscribe();
		try {
			await http.delete("/api/v1/push-subscriptions", { data: { endpoint } });
		} catch (e) { console.warn(e); }
	}
}
